const C = {
  bg: 'var(--bg)', surf: 'var(--surf)', surf2: 'var(--surf2)', border: 'var(--border)',
  text: 'var(--text)', muted: 'var(--muted)', dim: 'var(--dim)',
  accent: 'var(--accent)', accentBg: 'var(--accent-bg)', accentBorder: 'var(--accent-border)',
};
const AF = {
  h: "'Space Grotesk', system-ui, sans-serif",
  b: "'DM Sans', system-ui, sans-serif",
  m: "'DM Mono', 'Courier New', monospace",
};

function LegalNav() {
  return (
    <nav style={{
      position: 'sticky', top: 0, zIndex: 100,
      background: C.bg, borderBottom: `1px solid ${C.border}`,
      height: 64, display: 'flex', alignItems: 'center',
    }}>
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 24px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <a href="/" style={{ fontFamily: AF.h, fontSize: 20, fontWeight: 700, color: C.text, letterSpacing: '-0.5px', textDecoration: 'none' }}>
          Conjugr<span style={{ color: C.accent }}>8</span>
        </a>
        <a href="/learn-spanish" style={{ fontFamily: AF.b, fontSize: 14, color: C.muted, textDecoration: 'none' }}>
          Start learning →
        </a>
      </div>
    </nav>
  );
}

function LegalFooter() {
  return (
    <footer style={{ borderTop: `1px solid ${C.border}`, padding: '32px 24px', marginTop: 80 }}>
      <div style={{ maxWidth: 800, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
        <span style={{ fontFamily: AF.m, fontSize: 11, color: C.dim }}>© 2026 Conjugr8. All rights reserved.</span>
        <div style={{ display: 'flex', gap: 24 }}>
          <a href="/privacy" style={{ fontFamily: AF.b, fontSize: 12, color: C.muted, textDecoration: 'none' }}>Privacy</a>
          <a href="/terms" style={{ fontFamily: AF.b, fontSize: 12, color: C.muted, textDecoration: 'none' }}>Terms</a>
          <a href="/contact" style={{ fontFamily: AF.b, fontSize: 12, color: C.muted, textDecoration: 'none' }}>Contact</a>
        </div>
      </div>
    </footer>
  );
}

function Channel({ icon, label, value, href, note }: { icon: string; label: string; value: string; href: string; note?: string }) {
  return (
    <a href={href} style={{
      display: 'flex', alignItems: 'flex-start', gap: 18,
      padding: '24px 28px', borderRadius: 12,
      border: `1px solid ${C.accentBorder}`,
      background: C.accentBg,
      textDecoration: 'none',
      transition: 'border-color 0.15s',
    }}>
      <span style={{ fontSize: 28, lineHeight: 1, marginTop: 2 }}>{icon}</span>
      <div>
        <div style={{ fontFamily: AF.m, fontSize: 10, color: C.accent, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
        <div style={{ fontFamily: AF.h, fontSize: 17, fontWeight: 600, color: C.text, marginBottom: note ? 6 : 0 }}>{value}</div>
        {note && <div style={{ fontFamily: AF.b, fontSize: 13, color: C.muted }}>{note}</div>}
      </div>
    </a>
  );
}

export default function ContactPage() {
  return (
    <div style={{ background: C.bg, color: C.text, minHeight: '100vh' }}>
      <LegalNav />
      <main style={{ maxWidth: 680, margin: '0 auto', padding: '64px 24px 0' }}>
        <div style={{ marginBottom: 56 }}>
          <div style={{ fontFamily: AF.m, fontSize: 10, color: C.accent, letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 16 }}>
            Get in touch
          </div>
          <h1 style={{ fontFamily: AF.h, fontSize: 40, fontWeight: 700, color: C.text, letterSpacing: '-1px', marginBottom: 16 }}>
            Contact
          </h1>
          <p style={{ fontFamily: AF.b, fontSize: 17, color: C.muted, lineHeight: 1.7 }}>
            We're a small team. Email is the fastest way to reach us — we aim to reply within one business day.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 56 }}>
          <Channel
            icon="✉️"
            label="General enquiries"
            value="jobcrawls@gmail.com"
            href="mailto:jobcrawls@gmail.com"
            note="App feedback, feature requests, general questions"
          />
          <Channel
            icon="🔒"
            label="Privacy & data requests"
            value="jobcrawls@gmail.com"
            href="mailto:jobcrawls@gmail.com?subject=Privacy%20request"
            note="Data deletion, access requests, GDPR enquiries"
          />
          <Channel
            icon="🐛"
            label="Bug reports"
            value="jobcrawls@gmail.com"
            href="mailto:jobcrawls@gmail.com?subject=Bug%20report"
            note="Include your browser, OS, and steps to reproduce"
          />
        </div>

        <div style={{
          padding: '28px 32px', borderRadius: 12,
          background: C.surf, border: `1px solid ${C.border}`,
        }}>
          <h2 style={{ fontFamily: AF.h, fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 16 }}>
            Common questions
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[
              ['How do I delete my account and data?', 'Email jobcrawls@gmail.com with the subject "Delete my account". We\'ll process the request within 30 days.'],
              ['My progress disappeared. What happened?', 'Practice data is stored in your browser\'s local storage. Clearing browser data or switching browsers will reset it. Sign in to sync your progress to the cloud.'],
              ['The audio / AI features aren\'t working.', 'TTS and sentence generation require an active connection. If you see errors, try refreshing. If the issue persists, email us with your browser and a description of what you tried.'],
              ['Can I use Conjugr8 offline?', 'Drills and flashcards work offline once the app has loaded. AI sentence generation and pronunciation require a connection.'],
            ].map(([q, a]) => (
              <div key={q} style={{ paddingBottom: 16, borderBottom: `1px solid ${C.border}` }}>
                <div style={{ fontFamily: AF.b, fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 6 }}>{q}</div>
                <div style={{ fontFamily: AF.b, fontSize: 14, color: C.muted, lineHeight: 1.6 }}>{a}</div>
              </div>
            ))}
          </div>
        </div>
      </main>
      <LegalFooter />
    </div>
  );
}
